import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Building, Mail, Shield, KeyRound, Check, HardDrive } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { useAuth } from '../contexts/AuthContext.js';
import { Phone, FileText } from 'lucide-react';
import { maskPhone, maskCpfCnpj } from '../utils/masks.js';
import { GoogleDriveIntegration } from './GoogleDriveIntegration.js';
import { YouTubeIntegration } from './YouTubeIntegration.js';

export const MyAccount: React.FC = () => {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'profile' | 'google-drive' | 'youtube'>('profile');

  const [name, setName] = useState(user?.fullName || 'Gestor Instituto Saber');
  const [email] = useState(user?.email || 'gestor@institutosaber.com.br');
  const [document, setDocument] = useState('12.345.678/0001-90');
  const [phone, setPhone] = useState('(11) 98765-4321');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'youtube') {
      setActiveTab('youtube');
    } else if (tab === 'integracoes' || tab === 'google-drive') {
      setActiveTab('google-drive');
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'profile' | 'google-drive' | 'youtube') => {
    setActiveTab(tab);
    if (tab === 'youtube') {
      setSearchParams({ tab: 'youtube' });
    } else if (tab === 'google-drive') {
      setSearchParams({ tab: 'google-drive' });
    } else {
      setSearchParams({});
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <Header
        title="Minha Conta"
        subtitle="Gerencie seus dados de acesso, preferências e integrações com serviços externos"
      />

      <div className="p-8 max-w-6xl mx-auto space-y-6">
        {/* Sub-navegação em Abas */}
        <div className="flex items-center gap-2 border-b border-bdr pb-1 flex-wrap">
          <button
            onClick={() => handleTabChange('profile')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'profile'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <User size={16} /> Dados Cadastrais & Segurança
          </button>

          <button
            onClick={() => handleTabChange('google-drive')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'google-drive'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            <HardDrive size={16} /> Integrações → Google Drive
          </button>

          <button
            onClick={() => handleTabChange('youtube')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all border-b-2 ${
              activeTab === 'youtube'
                ? 'border-brand-primary text-brand-primary bg-orange-50/50'
                : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
            }`}
          >
            {/* YouTube Red Icon */}
            <svg className="w-4 h-4 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            Integrações → YouTube
          </button>
        </div>

        {activeTab === 'google-drive' ? (
          <GoogleDriveIntegration />
        ) : activeTab === 'youtube' ? (
          <YouTubeIntegration />
        ) : (
          <div className="space-y-6 max-w-4xl">
        <EduCard title="Dados Cadastrais">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Nome Completo
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-2.5 text-txt-muted" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface border border-bdr rounded-md text-txt-primary focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  E-mail de Acesso
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-2.5 text-txt-muted" />
                  <input
                    type="email"
                    disabled
                    value={email}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface-secondary border border-bdr rounded-md text-txt-secondary cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  CPF ou CNPJ
                </label>
                <div className="relative">
                  <FileText size={16} className="absolute left-3 top-2.5 text-txt-muted" />
                  <input
                    type="text"
                    maxLength={18}
                    value={document}
                    onChange={(e) => setDocument(maskCpfCnpj(e.target.value))}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface border border-bdr rounded-md text-txt-primary font-mono focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Telefone / Celular (WhatsApp)
                </label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-2.5 text-txt-muted" />
                  <input
                    type="text"
                    maxLength={15}
                    value={phone}
                    onChange={(e) => setPhone(maskPhone(e.target.value))}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface border border-bdr rounded-md text-txt-primary font-mono focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              {saved && (
                <span className="text-xs text-status-success font-medium flex items-center gap-1">
                  <Check size={14} /> Dados atualizados com sucesso!
                </span>
              )}
              <EduButton type="submit" variant="primary" size="sm" className="ml-auto">
                Salvar Alterações
              </EduButton>
            </div>
          </form>
        </EduCard>

        {/* Security & Password */}
        <EduCard title="Segurança e Senha">
          <div className="space-y-4">
            <p className="text-xs text-txt-secondary">
              A autenticação é protegida por Firebase Authentication com tokens seguros e expiração periódica.
            </p>
            <div className="flex items-center justify-between p-4 bg-surface-secondary border border-bdr rounded-lg">
              <div className="flex items-center gap-3">
                <KeyRound size={20} className="text-brand-primary" />
                <div>
                  <h4 className="text-xs font-bold text-txt-primary">Redefinição de Senha</h4>
                  <p className="text-[11px] text-txt-secondary">
                    Enviaremos um link de redefinição para o seu e-mail cadastrado.
                  </p>
                </div>
              </div>
              <EduButton
                variant="secondary"
                size="sm"
                onClick={() => alert(`Link de redefinição enviado para ${email}!`)}
              >
                Redefinir Senha
              </EduButton>
            </div>
          </div>
        </EduCard>

        {/* Logout Card */}
        <EduCard title="Encerrar Sessão">
          <div className="flex items-center justify-between">
            <p className="text-xs text-txt-secondary">
              Finaliza a sessão atual neste navegador.
            </p>
            <EduButton variant="destructive" size="sm" onClick={logout}>
              Sair da Conta
            </EduButton>
          </div>
        </EduCard>
      </div>
    )}
  </div>
</div>
  );
};
